/**
 * Animated ticket number roller
 * Creates a slot machine / water meter style animation
 * Shows real ticket numbers from the pool with adjacent tickets visible
 */

import { useEffect, useRef, useState, useMemo, useCallback, memo } from "react";
import styles from "./TicketRoller.module.css";

// =============================================================================
// Types
// =============================================================================

interface TicketRollerProps {
  /** Current ticket number being displayed */
  currentNumber: string | null;
  /** All possible ticket numbers from the raffle pool */
  ticketNumbers: string[];
  /** Whether the roller is spinning */
  isSpinning: boolean;
  /** Animation progress 0-100 */
  progress: number;
  /** Final winning number (only shown after animation) */
  winningNumber?: string | null;
  /** Size variant */
  size?: "normal" | "large";
  /** Callback when animation fully completes and final number is shown */
  onAnimationComplete?: () => void;
}

// =============================================================================
// Main Component
// =============================================================================

export const TicketRoller = memo(function TicketRoller({
  currentNumber,
  ticketNumbers,
  isSpinning,
  progress,
  winningNumber,
  size = "normal",
  onAnimationComplete,
}: TicketRollerProps) {
  // State for displayed ticket during animation
  const [displayedTicket, setDisplayedTicket] = useState<string | null>(
    currentNumber
  );
  const [displayedIndex, setDisplayedIndex] = useState<number>(-1);
  const intervalRef = useRef<number | null>(null);
  const onAnimationCompleteRef = useRef(onAnimationComplete);
  const isStopping = progress > 90;

  // Keep callback ref updated
  useEffect(() => {
    onAnimationCompleteRef.current = onAnimationComplete;
  }, [onAnimationComplete]);

  // Sorted ticket numbers for consistent ordering
  const sortedTickets = useMemo(() => {
    if (ticketNumbers.length === 0) return [];
    // Sort numerically
    return [...ticketNumbers].sort((a, b) => {
      const numA = parseInt(a, 10);
      const numB = parseInt(b, 10);
      return numA - numB;
    });
  }, [ticketNumbers]);

  // Find index of a ticket in the sorted array
  const findTicketIndex = useCallback(
    (ticket: string | null): number => {
      if (!ticket || sortedTickets.length === 0) return -1;
      return sortedTickets.findIndex((t) => t === ticket);
    },
    [sortedTickets]
  );

  // Get adjacent tickets (previous and next in the pool)
  const getAdjacentTickets = useCallback(
    (index: number) => {
      if (sortedTickets.length === 0 || index < 0) {
        return { prev: null, next: null };
      }

      const prevIndex =
        index <= 0 ? sortedTickets.length - 1 : index - 1;
      const nextIndex =
        index >= sortedTickets.length - 1 ? 0 : index + 1;

      return {
        prev: sortedTickets[prevIndex] || null,
        next: sortedTickets[nextIndex] || null,
      };
    },
    [sortedTickets]
  );

  // Format ticket number for display (pad with zeros)
  const formatTicket = useCallback(
    (ticket: string | null): string => {
      if (!ticket) return "0000";
      // Determine max length from pool
      const maxLen = sortedTickets.length > 0
        ? Math.max(4, Math.max(...sortedTickets.map((t) => t.length)))
        : 4;
      return ticket.padStart(maxLen, "0");
    },
    [sortedTickets]
  );

  // Random ticket from pool for spinning animation
  const getRandomTicket = useCallback((): { ticket: string; index: number } => {
    if (sortedTickets.length === 0) {
      return { ticket: "0000", index: -1 };
    }
    const randomIndex = Math.floor(Math.random() * sortedTickets.length);
    return { ticket: sortedTickets[randomIndex], index: randomIndex };
  }, [sortedTickets]);

  // Spinning animation effect
  useEffect(() => {
    if (isSpinning && !isStopping) {
      // Start spinning through random tickets from the pool
      const baseSpeed = 80;
      let currentSpeed = baseSpeed;

      const spin = () => {
        const { ticket, index } = getRandomTicket();
        setDisplayedTicket(ticket);
        setDisplayedIndex(index);

        intervalRef.current = window.setTimeout(spin, currentSpeed);
      };

      spin();

      return () => {
        if (intervalRef.current) {
          clearTimeout(intervalRef.current);
          intervalRef.current = null;
        }
      };
    } else if (isStopping) {
      // Slow down animation
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
        intervalRef.current = null;
      }

      let slowSpeed = 100;
      const slowDown = () => {
        slowSpeed += 60;
        if (slowSpeed < 500) {
          const { ticket, index } = getRandomTicket();
          setDisplayedTicket(ticket);
          setDisplayedIndex(index);
          intervalRef.current = window.setTimeout(slowDown, slowSpeed);
        } else {
          // Show final winning number
          const finalTicket = winningNumber || currentNumber;
          setDisplayedTicket(finalTicket);
          setDisplayedIndex(findTicketIndex(finalTicket));
          // Notify parent that animation is complete
          onAnimationCompleteRef.current?.();
        }
      };
      slowDown();

      return () => {
        if (intervalRef.current) {
          clearTimeout(intervalRef.current);
          intervalRef.current = null;
        }
      };
    } else {
      // Not spinning - show current number
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
        intervalRef.current = null;
      }
      const ticket = winningNumber || currentNumber;
      setDisplayedTicket(ticket);
      setDisplayedIndex(findTicketIndex(ticket));
    }
  }, [
    isSpinning,
    isStopping,
    currentNumber,
    winningNumber,
    getRandomTicket,
    findTicketIndex,
  ]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
      }
    };
  }, []);

  // Get adjacent tickets for water meter effect
  const { prev: prevTicket, next: nextTicket } = getAdjacentTickets(displayedIndex);

  // Check if we have real ticket data
  const hasTicketData = sortedTickets.length > 0;

  // Generate placeholder adjacent numbers when no real data
  const getPlaceholderAdjacent = (current: string | null, offset: number): string => {
    if (!current) return "0000";
    const num = parseInt(current, 10);
    if (isNaN(num)) return "0000";
    const adjacent = Math.max(0, num + offset);
    return adjacent.toString().padStart(4, "0");
  };

  // Determine what to show for adjacent tickets
  const prevDisplay = hasTicketData && prevTicket 
    ? formatTicket(prevTicket) 
    : getPlaceholderAdjacent(displayedTicket, -1);
  const nextDisplay = hasTicketData && nextTicket 
    ? formatTicket(nextTicket) 
    : getPlaceholderAdjacent(displayedTicket, 1);

  return (
    <div className={`${styles.roller} ${styles[size]}`}>
      <div className={styles.display}>
        {/* Previous ticket (partially visible above) - always show for water meter effect */}
        <div className={`${styles.ticketRow} ${styles.ticketAdjacent} ${styles.ticketPrev}`}>
          <span className={styles.prefix}>#</span>
          <span className={styles.ticketNumber}>{prevDisplay}</span>
        </div>

        {/* Current ticket */}
        <div
          className={`${styles.ticketRow} ${styles.ticketCurrent} ${isSpinning ? styles.spinning : ""} ${isStopping ? styles.stopping : ""}`}
        >
          <span className={styles.prefix}>#</span>
          <span className={styles.ticketNumber}>{formatTicket(displayedTicket)}</span>
        </div>

        {/* Next ticket (partially visible below) - always show for water meter effect */}
        <div className={`${styles.ticketRow} ${styles.ticketAdjacent} ${styles.ticketNext}`}>
          <span className={styles.prefix}>#</span>
          <span className={styles.ticketNumber}>{nextDisplay}</span>
        </div>
      </div>

      {/* Decorative elements */}
      <div className={styles.highlight} />
      <div className={styles.shadowTop} />
      <div className={styles.shadowBottom} />

      {/* Progress indicator */}
      {isSpinning && (
        <div className={styles.progressBar}>
          <div
            className={styles.progressFill}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
});

export default TicketRoller;
